import PaymentCategories from '../../enums/PaymentCategories';
import PaymentOptionsLoadingStatus from '../../enums/PaymentOptionsLoadingStatus';
import * as types from '../actions/action-types';

const getInitialState = () => ({
  paymentMethod: 'new card',
  paymentOptionName: 'new card',
  paymentOptionIndex: window.start?.settings?.should_defer_payment_option_preselection ? -1 : 0,
  category: PaymentCategories.NONE,
  availablePaymentCategories: [],
  shouldShowPaymentCategorySelectBoxes: false,
  availableCategoriesToPaymentOptionsMap: {},
  options: [],
  newCardDetails: {
    nameOnCard: window.start.addresses?.addresses?.find?.(address => address.addressId === '')?.addressee,
    useShippingAddress: true,
  },
  storedCV2Details: {},
  klarnaDetails: {},
  googlePayPaymentData: {
    paymentMethodData: {
      tokenizationData: {},
      info: {
        billingAddress: {},
      },
    },
  },
  isGooglePayAvailable: false,
  isApplePayAvailable: false,
  threeDSecureData: {
    shouldShowChallengeIframe: false,
  },
  optionalPaymentDetailsConsentConfiguration: {},
  adyenV2: {
    sessionData: null,
  },
  optty: {
    sessionData: null,
  },
  errorMessages: [],
  translatedErrorMessages: [],
  isPaymentContainerCollapsed: false,
  paymentOptionsLoadingStatus: PaymentOptionsLoadingStatus.LOADING,
});

const paymentReducer = (state = getInitialState(), action) => {
  switch (action.type) {
    case types.SET_PAYMENT_OPTIONS: {
      const updatedState = {};
      updatedState.options = action.options;
      updatedState.errorMessages = new Array(action.options.length);
      updatedState.translatedErrorMessages = new Array(action.options.length);
      
      if (!action.options.some(option => option.type === 'ADYEN_V2')) {
        updatedState.paymentOptionsLoadingStatus = PaymentOptionsLoadingStatus.LOADED;
      }
      
      return {...state, ...updatedState};
    }
  
    case types.ADD_PAYMENT_OPTIONS_TO_STATE: {
      const shouldAddAdyenPaymentOptionsToTop = window.start.settings?.should_add_adyen_payment_options_to_top;
      let updatedOptions = state.options;
      let indexToRemove;
      let updatedStateOptions;
      let updatedStateErrorMessages;
      let updatedStateTranslatedErrorMessages;
      
      if (action.optionTypeToRemove) {
        indexToRemove = updatedOptions.findIndex(option => option.type === action.optionTypeToRemove);
        updatedOptions = updatedOptions.filter(option => option.type !== action.optionTypeToRemove);
      }
  
      if (shouldAddAdyenPaymentOptionsToTop) {
        updatedStateOptions = [...action.options, ...updatedOptions];
        updatedStateErrorMessages = [...action.options.map(() => false), ...state.errorMessages].filter((_, index) => index !== indexToRemove);
        updatedStateTranslatedErrorMessages = [...action.options.map(() => false), ...state.translatedErrorMessages].filter((_, index) => index !== indexToRemove);
      } else {
        updatedStateOptions = updatedOptions.concat(action.options);
        updatedStateErrorMessages = [...state.errorMessages, ...action.options.map(() => false)].filter((_, index) => index !== indexToRemove);
        updatedStateTranslatedErrorMessages = [...state.translatedErrorMessages, ...action.options.map(() => false)].filter((_, index) => index !== indexToRemove);
      }
      
      const updatedState = {};
      updatedState.options = updatedStateOptions;
      updatedState.errorMessages = updatedStateErrorMessages;
      updatedState.translatedErrorMessages = updatedStateTranslatedErrorMessages;
      
      return {
        ...state,
        ...updatedState,
      };
    }
  
    case types.SET_PAYMENT_OPTION_ERROR_MESSAGE: {
      const updatedErrorMessage = [...state.errorMessages];
      updatedErrorMessage[action.paymentOptionIndex] = action.errorMessage;
      return {...state, errorMessages: updatedErrorMessage};
    }
    
    case types.SET_TRANSLATED_PAYMENT_OPTION_ERROR_MESSAGE: {
      const updatedTranslatedErrorMessage = [...state.translatedErrorMessages];
      updatedTranslatedErrorMessage[action.paymentOptionIndex] = action.translatedErrorMessage;
      return {...state, translatedErrorMessages: updatedTranslatedErrorMessage};
    }
    
    case types.UPDATE_NEW_CARD_DETAILS: {
      const updatedDetails = {
        ...state.newCardDetails,
        [action.newCardProperty]: action.propertyValue,
      }; // update just the given property
      return {...state, newCardDetails: updatedDetails};
    }
    
    case types.UPDATE_CV2_DETAILS: {
      const updatedDetails = {
        ...state.storedCV2Details,
        [action.cv2Property]: action.propertyValue,
      }; // update just the given property
      return {...state, storedCV2Details: updatedDetails};
    }
    
    case types.SET_CREDIT_ZERO_ORDER: {
      return {...state, paymentMethod: 'credit'};
    }
    
    case types.SET_GOOGLE_AVAILABLE: {
      return {...state, isGooglePayAvailable: true};
    }
    
    case types.SET_APPLEPAY_AVAILABLE: {
      return {...state, isApplePayAvailable: true};
    }
    
    case types.UPDATE_KLARNA_DETAILS: {
      return {
        ...state,
        klarnaDetails: {
          ...state.klarnaDetails,
          [action.klarnaProperty]: action.propertyValue,
        },
      };
    }
    
    case types.SET_PAYMENT_CATEGORY: {
      return {...state, category: action.category};
    }
  
    case types.SET_AVAILABLE_PAYMENT_CATEGORIES: {
      return {...state, availablePaymentCategories: action.availablePaymentCategories};
    }
    
    case types.SET_PAYMENT_OPTIONS_FOR_CATEGORY: {
      return {...state, availableCategoriesToPaymentOptionsMap: action.availableCategoriesToPaymentOptionsMap};
    }
    
    case types.SET_SHOULD_SHOW_PAYMENT_CATEGORY_SELECT_BOXES: {
      return {...state, shouldShowPaymentCategorySelectBoxes: action.shouldShowPaymentCategorySelectBoxes};
    }
    
    case types.SET_PAYMENT_OPTION_INDEX: {
      if (action.paymentOptionIndex < state.options.length) {
        const newValues = {
          paymentOptionIndex: action.paymentOptionIndex,
          paymentOptionName: action.paymentOptionIndex === -1 ? 'CREDIT' : state.options[action.paymentOptionIndex]?.displayId || state.options[action.paymentOptionIndex]?.name,
        };
        return {...state, ...newValues};
      }
      return state;
    }
    
    case types.SET_SUB_PAYMENT_OPTION_INDEX: {
      const newPaymentOptions = [...state.options];
      const newPaymentOption = {...newPaymentOptions[action.paymentOptionIndex]};
      
      newPaymentOption.selectedSubPaymentOptionIndex = action.selectedSubPaymentOptionIndex;
      newPaymentOptions[action.paymentOptionIndex] = newPaymentOption;
      
      return {
        ...state,
        options: newPaymentOptions,
      };
    }
    
    case types.SET_GOOGLE_PAY_PAYMENT_DATA: {
      return {...state, googlePayPaymentData: action.googlePayPaymentData};
    }
    
    case types.INITIATE_DEVICE_DATA_COLLECTION: {
      return {
        ...state,
        threeDSecureData: {
          ...action.threeDSecureData,
          collect: true,
        },
      };
    }
  
    case types.UPDATE_THREEDS_DATA_FOR_CHALLENGE_IFRAME: {
      return {
        ...state,
        threeDSecureData: {
          ...action.threeDSecureData,
        },
      };
    }
    
    case types.DEVICE_DATA_COLLECTION_COMPLETE: {
      return {
        ...state,
        threeDSecureData: {
          ...state.threeDSecureData,
          collect: false,
        },
      };
    }
    
    case types.UPDATE_SHOULD_STORE_PAYMENT_DETAILS_CONSENT: {
      const newOptionalPaymentDetailsConsentConfiguration = {
        ...state.optionalPaymentDetailsConsentConfiguration,
        [action.paymentMethodType]: !state.optionalPaymentDetailsConsentConfiguration[action.paymentMethodType],
      };
      
      return {
        ...state,
        optionalPaymentDetailsConsentConfiguration: newOptionalPaymentDetailsConsentConfiguration,
      };
    }
  
    case types.SET_OPTIONAL_PAYMENT_DETAILS_CONSENT_CONFIGURATION: {
      const newOptionalPaymentDetailsConsentConfiguration = {
        ...state.optionalPaymentDetailsConsentConfiguration,
      };
      
      Object.entries(action.optionalPaymentDetailsConsentConfiguration)
        .forEach((entry) => {
          if (entry[1]) {
            newOptionalPaymentDetailsConsentConfiguration[entry[0]] = false;
          }
        });
      
      return {
        ...state,
        optionalPaymentDetailsConsentConfiguration: newOptionalPaymentDetailsConsentConfiguration,
      };
    }
  
    case types.UPDATE_IS_PAYMENT_CONTAINER_COLLAPSED: {
      return {
        ...state,
        isPaymentContainerCollapsed: action.expanded,
      };
    }
    
    case types.SET_ADYEN_V2_SESSION_DATA: {
      return {
        ...state,
        adyenV2: {
          ...state.adyenV2,
          sessionData: action.sessionData,
        },
      };
    }
    
    case types.SET_ADYEN_V2_PLACE_ORDER_DATA: {
      return {
        ...state,
        adyenV2: {
          ...state.adyenV2,
          placeOrderData: action.placeOrderData,
          billingAddress: action.billingAddress,
        },
      };
    }
  
    case types.SET_ADYEN_V2_CHECKOUT: {
      return {
        ...state,
        adyenV2: {
          ...state.adyenV2,
          adyenV2Checkout: action.adyenV2Checkout,
        },
      };
    }
  
    case types.UPDATE_SHOULD_SHOW_THREEDS_CHALLENGE_IFRAME: {
      return {
        ...state,
        threeDSecureData: {
          ...state.threeDSecureData,
          shouldShowChallengeIframe: action.shouldShow,
        },
      };
    }
  
    case types.SET_NEWPAY_SESSION_DATA: {
      return {
        ...state,
        newpay: {
          ...state.newpay,
          sessionData: action.sessionData,
        },
      };
    }
    
    case types.SET_NEWPAY_SCRIPT_FAILURE: {
      return {
        ...state,
        newpay: {
          ...state.newpay,
          scriptLoadFailure: action.scriptLoadFailure,
        },
      };
    }
  
    case types.SET_NEWPAY_WALLET: {
      return {
        ...state,
        newpay: {
          ...state.newpay,
          wallet: action.wallet,
        },
      };
    }
  
    case types.SET_PAYMENT_OPTIONS_LOADING_STATUS: {
      return {
        ...state,
        paymentOptionsLoadingStatus: action.paymentOptionsLoadingStatus,
      };
    }
    default:
      return state;
  }
};

export default paymentReducer;
